import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/client.py ClientOut.
export interface Client {
  usr_id: number;
  username: string;
  email: string | null;
  company: string | null;
  phone: string | null;
  credit: number;
  channel: number;
  plan_id: number | null;
  billing_mode: string; // "flat" | "npa_nxx"
  card_payment_enabled?: boolean;
  card_payment_max_amount?: number | null;
  active: boolean;
}

export interface ClientListParams {
  page?: number;
  per_page?: number;
  active_only?: boolean;
}

export async function listClients(params: ClientListParams): Promise<Paginated<Client>> {
  const resp = await api.get<Paginated<Client>>("/clients/", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load clients");
  return resp.data;
}

// Account fields only — credit adjustments are intentionally NOT exposed here
// (real money; handled separately with explicit confirmation).
export interface ClientInput {
  username?: string;
  email?: string | null;
  company?: string | null;
  phone?: string | null;
  plan_id?: number | null;
  billing_mode?: string; // "flat" | "npa_nxx"
  card_payment_enabled?: boolean;
  card_payment_max_amount?: number | null;
  channel?: number;
}

export async function createClient(body: ClientInput & { username: string }): Promise<number> {
  const resp = await api.post<APIResponse<{ usr_id: number }>>("/clients/", body);
  return unwrap(resp.data).usr_id;
}

export async function updateClient(usrId: number, body: ClientInput): Promise<void> {
  await api.put(`/clients/${usrId}`, body);
}

export async function deactivateClient(usrId: number): Promise<void> {
  await api.delete(`/clients/${usrId}`);
}

// Manual credit top-up (internal ledger; positive amounts only). Writes an
// audited payment row server-side. NOT a card charge.
export async function addClientCredit(
  usrId: number,
  body: { amount: number; description: string },
): Promise<void> {
  await api.post(`/clients/${usrId}/credit/add`, body);
}
